// packages
import { reactive, toRefs, onMounted } from '@vue/composition-api';

// helpers
import { get, all } from '@/helpers/requests';

/**
 * Definição das configurações para a obtenção
 * da listagem de campanhas.
 * Caso não tenha permissões para adicionar / editar
 * é enviado as configs vazias, de forma a não obter as
 * listagens a API.
 *
 * @category Hook
 * @param {Object} props
 * @param {Route} route
 * @returns {Object}
 * @since 1.2.7
 *
 */
const generateConfigsToCampaigns = allowFilling => (
    allowFilling ? structureCampaigns() : []
);

/**
 *
 * Estruturação do pedido da listagem
 * de campanhas.
 *
 * @category Hook
 * @param {Object} props
 * @param {Route} route
 * @returns {Object}
 * @since 1.2.7
 *
 */
const structureCampaigns = () => {
    return [ { endpoint: 'campaigns' } ];
};

/**
 * Definição das configurações para a obtenção
 * das listagens de utilizadores && clientes.
 * Caso não tenha permissões para adicionar / editar
 * é enviado as configs vazias, de forma a não obter as
 * listagens a API.
 *
 * @category Hook
 * @param {Object} props
 * @param {Route} route
 * @returns {Object}
 * @since 1.0.0
 *
 */
const generateConfigsToUsersAndClients = ( allowFilling, configs ) => (
    allowFilling ? structureUsersAndClients( configs.props, configs.route, configs.params ) : []
);

/**
 *
 * Estruturação do pedido das duas listagens
 * de utilizadores e clientes.
 *
 * @category Hook
 * @param {Object} props
 * @param {Route} route
 * @returns {Object}
 * @since 1.0.0
 *
 */
const structureUsersAndClients = ( props, route, params = {}) => {
    const isEdition = !route.meta.isAdd;
    // caso seja uma página singular, o pedido
    // das listagens só deve ser executado quando já
    // existe o modelo.
    if ( isEdition && !Object.keys( props.model ).length )
        return [];

    return [
        { endpoint: 'users', ...( params.users && { params: params.users }) },
        { endpoint: 'clients', ...( params.clients && { params: params.clients }) }
    ];
};

/**
 *
 * Iniciação e configuração dos pedidos de listagens API.
 * Conform o númer de listagens, é criada a estrutura de
 * informação para às mesmas.
 *
 * @category Hook
 * @param {Array} configs
 * @param {String} configs[0].endpoint
 * @param {Object} configs[0].params
 * @param {Object} extraState
 * @returns {Object}
 * @since 1.0.0
 *
 */
const useLists = ( configs, extraState = {}) => {
    const state = reactive({
        order: {
            loading: true
        },
        // definição das listagens necessárias.
        ...( configs.length && {
            ...configs.reduce( ( key, list ) => {
                key[list.endpoint] = [];
                return key;
            }, {})
        }),
        ...extraState
    });

    onMounted( async () => {
        // se não tiver configurações
        // não vale a pena fazer o pedido a API.
        if ( !configs.length ) {
            state.order.loading = false;
            return;
        }

        // obtenção das listagens.
        const { lists } = await getLists({ configs, order: state.order });
        // associação das listagens obtidas ao state.
        configs.forEach( ({ endpoint }) => { state[endpoint] = lists[endpoint]; });
    });

    return { ...toRefs( state ) };
};

/**
 *
 * Obtenção das listagens que estão
 * presentes nas páginas. É feito o tratamento da informação
 * de forma a ser apresentado nas diversas drops.
 *
 * @category Hook
 * @param {Array} object.endpoints
 * @param {Object} object.order
 * @returns {Object}
 * @since 1.0.0
 *
 */
const getLists = async ({ configs, order }) => {
    // criação dos requests.
    const requests = configs.map( ({ endpoint, params }) => get( `/${endpoint}`, params || {}) );

    const lists = await all( requests )
        .then( response => {
            const listsBuilder = {};

            // construção do objecto de listagens.
            configs.forEach( ({ endpoint }, index ) => {
                const { data } = response[index].data.content;
                // estrutura base para as drops
                listsBuilder[endpoint] = data.map( d => ({ value: d.id, label: d.name }) );
            });

            return listsBuilder;
        }).catch( error => error );

    order.loading = false;

    return { lists };
};

export {
    generateConfigsToCampaigns,
    structureCampaigns,
    generateConfigsToUsersAndClients,
    structureUsersAndClients,
    useLists,
    getLists
};